import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import useElementToPdf from '../utils/useElementToPdf';
import { 
  Shield, 
  Users, 
  Check, 
  X, 
  Edit2, 
  RotateCcw,
  History,
  Printer,
  Search,
  Filter,
  User,
  Clock,
  AlertTriangle,
  Save,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Send,
  Copy
} from 'lucide-react';

const PermissionsManagement = () => {
  const { user, isAdmin } = useAuth();
  const { language } = useLanguage();
  const [users, setUsers] = useState([]);
  const [sections, setSections] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [saving, setSaving] = useState(false);
  const [approvalMode, setApprovalMode] = useState('direct'); // direct = حفظ واعتماد, pending = حفظ للموافقة
  const printRef = useRef();
  
  // تحقق هل المستخدم مدير
  const canEditPermissions = isAdmin();

  useEffect(() => {
    fetchData();
    if (canEditPermissions) {
      fetchPendingRequests();
    }
  }, [canEditPermissions]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, sectionsRes] = await Promise.all([
        api.get('/permissions/users'),
        api.get('/permissions/sections')
      ]);
      setUsers(usersRes.data);
      setSections(sectionsRes.data);
    } catch (error) {
      toast.error('فشل تحميل البيانات');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const res = await api.get('/permissions/pending');
      setPendingRequests(res.data);
    } catch (error) {
      console.error('Failed to fetch pending requests:', error);
    }
  };

  const fetchLogs = async (userId = null) => {
    try {
      const params = userId ? `?user_id=${userId}` : '';
      const res = await api.get(`/permissions/logs${params}`);
      setLogs(res.data.logs);
      setShowLogsModal(true);
    } catch (error) {
      toast.error('فشل تحميل السجل');
    }
  };

  const openEditModal = (userData) => {
    setSelectedUser(userData.user);
    setEditingPermissions(JSON.parse(JSON.stringify(userData.permissions)));
    setExpandedSections({});
    setShowEditModal(true);
  };

  // ===== Copy permissions from another user =====
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyTargetUser, setCopyTargetUser] = useState(null);
  const [copySourceId, setCopySourceId] = useState('');
  const [copyLoading, setCopyLoading] = useState(false);

  const openCopyModal = (userData) => {
    setCopyTargetUser(userData.user);
    setCopySourceId('');
    setShowCopyModal(true);
  };

  const handleCopyPermissions = async () => {
    if (!copySourceId || !copyTargetUser) {
      toast.error('اختر مستخدماً مصدراً أولاً');
      return;
    }
    if (copySourceId === copyTargetUser.id) {
      toast.error('لا يمكن النسخ من المستخدم لنفسه');
      return;
    }
    setCopyLoading(true);
    try {
      const res = await api.post(`/permissions/copy/${copySourceId}/to/${copyTargetUser.id}`);
      toast.success(res.data?.message || 'تم نسخ الصلاحيات بنجاح');
      setShowCopyModal(false);
      setCopyTargetUser(null);
      setCopySourceId('');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل النسخ');
    } finally {
      setCopyLoading(false);
    }
  };

  const handlePermissionChange = (section, action, value) => {
    setEditingPermissions(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [action]: value
      }
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedUser || !editingPermissions) return;
    
    if (!canEditPermissions) {
      toast.error('فقط المدير يستطيع تعديل الصلاحيات');
      return;
    }
    
    try {
      setSaving(true);
      const res = await api.put(`/permissions/user/${selectedUser.id}`, {
        user_id: selectedUser.id,
        permissions: editingPermissions,
        approval_mode: approvalMode
      });
      
      if (res.data.status === 'pending') {
        toast.success(`تم إرسال طلب تغيير صلاحيات ${selectedUser.name} للموافقة`);
        fetchPendingRequests();
      } else {
        toast.success(`تم تحديث صلاحيات ${selectedUser.name} بنجاح`);
      }
      
      setShowEditModal(false);
      setApprovalMode('direct');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حفظ الصلاحيات');
    } finally {
      setSaving(false);
    }
  };

  const handleApprovePending = async (requestId) => {
    try {
      await api.post(`/permissions/pending/${requestId}/approve`);
      toast.success('تمت الموافقة على التغييرات');
      fetchPendingRequests();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل الموافقة على الطلب');
    }
  };

  const handleRejectPending = async (requestId) => {
    try {
      await api.post(`/permissions/pending/${requestId}/reject`);
      toast.success('تم رفض الطلب');
      fetchPendingRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل رفض الطلب');
    }
  };

  const handleResetPermissions = async (userId, userName) => {
    if (!window.confirm(`هل أنت متأكد من إعادة تعيين صلاحيات ${userName} للافتراضية؟`)) return;
    
    try {
      await api.delete(`/permissions/user/${userId}`);
      toast.success(`تم إعادة تعيين صلاحيات ${userName}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إعادة التعيين');
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-green-500/20 text-green-500',
      barber: 'bg-blue-500/20 text-blue-500',
      guest: 'bg-gray-500/20 text-gray-500'
    };
    const labels = {
      admin: 'مدير',
      barber: 'حلاق',
      guest: 'زائر'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[role] || styles.guest}`}>
        {labels[role] || role}
      </span>
    );
  };

  const getPermissionCount = (permissions) => {
    let granted = 0;
    let total = 0;
    
    Object.values(permissions).forEach(section => {
      ['view', 'add', 'edit', 'delete'].forEach(action => {
        total++;
        if (section[action]) granted++;
      });
    });
    
    return { granted, total };
  };

  // react-to-print hook
  const handlePrint = useElementToPdf({
    contentRef: printRef,
    documentTitle: 'سجل_تغييرات_الصلاحيات',
    pageStyle: `
      @page { size: A4; margin: 15mm; }
      @media print {
        body { 
          -webkit-print-color-adjust: exact !important; 
          print-color-adjust: exact !important;
          direction: rtl;
          font-family: 'Segoe UI', Tahoma, sans-serif;
        }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ddd; padding: 10px; text-align: right; }
        th { background: #f5f5f5; }
      }
    `
  });

  const filteredUsers = users.filter(u => 
    u.user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            إدارة الصلاحيات
          </h1>
          <p className="text-muted-foreground mt-1">تفويض وإدارة صلاحيات المستخدمين</p>
        </div>
        <div className="flex items-center gap-3">
          {canEditPermissions && pendingRequests.length > 0 && (
            <Button
              onClick={() => setShowPendingModal(true)}
              variant="outline"
              className="gap-2 border-yellow-500 text-yellow-500"
              data-testid="view-pending-btn"
            >
              <Clock className="w-4 h-4" />
              طلبات معلقة ({pendingRequests.length})
            </Button>
          )}
          <Button
            onClick={() => fetchLogs()}
            variant="outline"
            className="gap-2"
            data-testid="view-logs-btn"
          >
            <History className="w-4 h-4" />
            سجل التغييرات
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
        <input
          type="text"
          placeholder="بحث بالاسم أو البريد الإلكتروني..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pr-10 pl-4 py-3 bg-secondary/50 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="search-users-input"
        />
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredUsers.map((userData) => {
          const { granted, total } = getPermissionCount(userData.permissions);
          const percentage = Math.round((granted / total) * 100);
          
          return (
            <Card key={userData.user.id} className="border-white/10 hover:border-primary/30 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{userData.user.name}</h3>
                      <p className="text-sm text-muted-foreground">{userData.user.email}</p>
                    </div>
                  </div>
                  {getRoleBadge(userData.user.role)}
                </div>

                {/* Permissions Progress */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">الصلاحيات الممنوحة</span>
                    <span className="font-medium">{granted}/{total}</span>
                  </div>
                  <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>

                {/* Custom Badge */}
                {userData.is_custom && (
                  <div className="flex items-center gap-2 text-yellow-500 text-sm mb-4">
                    <AlertTriangle className="w-4 h-4" />
                    <span>صلاحيات مخصصة</span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {canEditPermissions ? (
                    <>
                      <Button
                        onClick={() => openEditModal(userData)}
                        size="sm"
                        className="flex-1 gap-2"
                        data-testid={`edit-permissions-${userData.user.id}`}
                        disabled={userData.user.id === user?.id}
                      >
                        <Edit2 className="w-4 h-4" />
                        تعديل
                      </Button>
                      {userData.is_custom && (
                        <Button
                          onClick={() => handleResetPermissions(userData.user.id, userData.user.name)}
                          size="sm"
                          variant="outline"
                          className="gap-2"
                          data-testid={`reset-permissions-${userData.user.id}`}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        onClick={() => openCopyModal(userData)}
                        size="sm"
                        variant="outline"
                        className="gap-2 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                        title="نسخ صلاحيات مستخدم آخر"
                        data-testid={`copy-permissions-${userData.user.id}`}
                        disabled={userData.user.id === user?.id}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => openEditModal(userData)}
                      size="sm"
                      variant="outline"
                      className="flex-1 gap-2"
                      disabled={userData.user.id !== user?.id}
                    >
                      <Eye className="w-4 h-4" />
                      عرض صلاحياتي
                    </Button>
                  )}
                  <Button
                    onClick={() => fetchLogs(userData.user.id)}
                    size="sm"
                    variant="ghost"
                    className="gap-2"
                    data-testid={`view-user-logs-${userData.user.id}`}
                    disabled={!canEditPermissions && userData.user.id !== user?.id}
                  >
                    <History className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit Permissions Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-card border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-primary" />
              تعديل صلاحيات: {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {sections.map((section) => {
              const sectionPerms = editingPermissions?.[section.key] || {};
              const isExpanded = expandedSections[section.key];
              
              return (
                <div key={section.key} className="border border-white/10 rounded-lg overflow-hidden">
                  {/* Section Header */}
                  <div 
                    className="flex items-center justify-between p-4 bg-secondary/30 cursor-pointer hover:bg-secondary/50"
                    onClick={() => toggleSection(section.key)}
                  >
                    <div>
                      <h4 className="font-medium">{section.name}</h4>
                      <p className="text-sm text-muted-foreground">{section.description}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        {sectionPerms.view && <span className="text-xs bg-green-500/20 text-green-500 px-2 py-1 rounded">عرض</span>}
                        {sectionPerms.add && <span className="text-xs bg-blue-500/20 text-blue-500 px-2 py-1 rounded">إضافة</span>}
                        {sectionPerms.edit && <span className="text-xs bg-yellow-500/20 text-yellow-500 px-2 py-1 rounded">تعديل</span>}
                        {sectionPerms.delete && <span className="text-xs bg-red-500/20 text-red-500 px-2 py-1 rounded">حذف</span>}
                      </div>
                      {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </div>

                  {/* Section Permissions */}
                  {isExpanded && (
                    <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                        <span className="text-sm">عرض</span>
                        <Switch
                          checked={sectionPerms.view || false}
                          onCheckedChange={(value) => handlePermissionChange(section.key, 'view', value)}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                        <span className="text-sm">إضافة</span>
                        <Switch
                          checked={sectionPerms.add || false}
                          onCheckedChange={(value) => handlePermissionChange(section.key, 'add', value)}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                        <span className="text-sm">تعديل</span>
                        <Switch
                          checked={sectionPerms.edit || false}
                          onCheckedChange={(value) => handlePermissionChange(section.key, 'edit', value)}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                        <span className="text-sm">حذف</span>
                        <Switch
                          checked={sectionPerms.delete || false}
                          onCheckedChange={(value) => handlePermissionChange(section.key, 'delete', value)}
                        />
                      </div>
                      
                      {/* Advanced Options */}
                      {(section.key === 'revenues' || section.key === 'expenses' || section.key === 'queue') && (
                        <>
                          <div className="col-span-2 flex items-center justify-between p-3 bg-yellow-500/10 rounded-lg">
                            <span className="text-sm">تعديل الخاص فقط</span>
                            <Switch
                              checked={sectionPerms.edit_own_only || false}
                              onCheckedChange={(value) => handlePermissionChange(section.key, 'edit_own_only', value)}
                            />
                          </div>
                          <div className="col-span-2 flex items-center justify-between p-3 bg-yellow-500/10 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              <span className="text-sm">حد زمني للتعديل (ساعات)</span>
                            </div>
                            <input
                              type="number"
                              min="0"
                              max="24"
                              value={sectionPerms.edit_time_limit || 0}
                              onChange={(e) => handlePermissionChange(section.key, 'edit_time_limit', parseInt(e.target.value) || 0)}
                              className="w-20 px-2 py-1 bg-secondary border border-white/10 rounded text-center"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              إلغاء
            </Button>
            
            {canEditPermissions ? (
              <>
                {/* خيار حفظ للموافقة من شخص آخر */}
                <Button 
                  onClick={() => {
                    setApprovalMode('pending');
                    handleSavePermissions();
                  }} 
                  disabled={saving}
                  variant="outline"
                  className="gap-2 border-yellow-500 text-yellow-500 hover:bg-yellow-500/10"
                  data-testid="save-pending-btn"
                >
                  {saving && approvalMode === 'pending' ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-yellow-500"></div>
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  حفظ للموافقة
                </Button>
                
                {/* خيار حفظ واعتماد فوري */}
                <Button 
                  onClick={() => {
                    setApprovalMode('direct');
                    handleSavePermissions();
                  }} 
                  disabled={saving}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                  data-testid="save-approve-btn"
                >
                  {saving && approvalMode === 'direct' ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  حفظ واعتماد
                </Button>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">
                للتعديل، يرجى التواصل مع المدير
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Permissions Modal */}
      <Dialog open={showCopyModal} onOpenChange={setShowCopyModal}>
        <DialogContent className="bg-card border-white/10 max-w-md" data-testid="copy-permissions-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Copy className="w-6 h-6 text-emerald-500" />
              نسخ صلاحيات من مستخدم آخر
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-sm text-muted-foreground">
              المستخدم المستهدف: <span className="font-bold text-foreground">{copyTargetUser?.name}</span>
              <span className="text-xs ms-2 px-2 py-0.5 rounded bg-secondary/60">{copyTargetUser?.role}</span>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                اختر المستخدم المصدر — ستُنسخ صلاحياته بالكامل:
              </label>
              <select
                value={copySourceId}
                onChange={(e) => setCopySourceId(e.target.value)}
                className="w-full bg-secondary/50 border border-white/10 rounded-md px-3 py-2 text-foreground"
                data-testid="copy-source-select"
              >
                <option value="">-- اختر مستخدم --</option>
                {users
                  .filter((u) => u.user.id !== copyTargetUser?.id)
                  .map((u) => (
                    <option key={u.user.id} value={u.user.id}>
                      {u.user.name} ({u.user.role}) — {u.grant_count || 0}/100
                    </option>
                  ))}
              </select>
            </div>

            <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded p-3">
              ⚠️ سيتم استبدال الصلاحيات الحالية لـ <strong>{copyTargetUser?.name}</strong> بالكامل
              بصلاحيات المستخدم المصدر. هذا الإجراء قابل للإلغاء عبر "إعادة تعيين".
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCopyModal(false)} disabled={copyLoading}>
              إلغاء
            </Button>
            <Button
              onClick={handleCopyPermissions}
              disabled={copyLoading || !copySourceId}
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
              data-testid="confirm-copy-permissions-btn"
            >
              <Copy className="w-4 h-4" />
              {copyLoading ? 'جاري النسخ...' : 'نسخ الآن'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logs Modal */}
      <Dialog open={showLogsModal} onOpenChange={setShowLogsModal}>
        <DialogContent className="bg-card border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-3">
                <History className="w-6 h-6 text-primary" />
                سجل تغييرات الصلاحيات (آخر شهرين)
              </span>
              <Button onClick={handlePrint} size="sm" variant="outline" className="gap-2">
                <Printer className="w-4 h-4" />
                تنزيل PDF
              </Button>
            </DialogTitle>
          </DialogHeader>

          <div ref={printRef} className="py-4">
            {logs.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">لا توجد سجلات</p>
            ) : (
              <table className="w-full">
                <thead className="bg-secondary/50">
                  <tr>
                    <th className="px-4 py-3 text-right text-sm font-semibold">التاريخ</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">المدير</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">المستخدم</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">الإجراء</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">التفاصيل</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="border-b border-white/5">
                      <td className="px-4 py-3 text-sm">
                        {new Date(log.created_at).toLocaleString('ar-SA')}
                      </td>
                      <td className="px-4 py-3 text-sm">{log.admin_name}</td>
                      <td className="px-4 py-3 text-sm">{log.target_user_name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          log.action === 'update' ? 'bg-blue-500/20 text-blue-500' :
                          log.action === 'reset' ? 'bg-yellow-500/20 text-yellow-500' :
                          'bg-gray-500/20 text-gray-500'
                        }`}>
                          {log.action === 'update' ? 'تحديث' : log.action === 'reset' ? 'إعادة تعيين' : log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{log.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pending Requests Modal */}
      <Dialog open={showPendingModal} onOpenChange={setShowPendingModal}>
        <DialogContent className="bg-card border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-yellow-500" />
              طلبات تغيير الصلاحيات المعلقة
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {pendingRequests.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">لا توجد طلبات معلقة</p>
            ) : (
              pendingRequests.map((request) => (
                <div key={request.id} className="border border-white/10 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold">{request.user_name}</h4>
                      <p className="text-sm text-muted-foreground">
                        طلب من: {request.requested_by_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(request.created_at).toLocaleDateString('ar-AE', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <span className="px-2 py-1 bg-yellow-500/20 text-yellow-500 rounded text-xs">
                      معلق
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleApprovePending(request.id)}
                      size="sm"
                      className="gap-2 bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4" />
                      موافقة
                    </Button>
                    <Button
                      onClick={() => handleRejectPending(request.id)}
                      size="sm"
                      variant="destructive"
                      className="gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      رفض
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PermissionsManagement;
